import React, { useState, useEffect, useRef } from 'react';
import LocationSearch from './common/LocationSearch';
import firmService from '../services/firmService';

const Section = ({ title, children, action }) => (
    <div>
        <div className="flex items-center justify-between mb-2.5">
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{title}</h4>
            {action}
        </div>
        {children}
    </div>
);

export default function FirmModal({ firm, onSave, onClose, onDelete, onImportCsv }) {
    const isEditing = Boolean(firm);
    const formatDate = (d) => d ? (typeof d === 'string' && d.length > 10 ? d.slice(0, 10) : d) : '';

    const [formData, setFormData] = useState(firm ? {
        ...firm,
        bonusDate: formatDate(firm.bonusDate || firm.bonus_date || ''),
    } : {
        name: '', founded: new Date().getFullYear(), totalEmployees: '', strategies: [],
        offices: [{ city: '', country: '', isPrimary: true }],
        payoutStructure: '', bonusDate: '', aum: '', notes: '', pnlPayoutFrequency: '', putUpCapital: null,
        linkedinUrl: '', website: '',
    });
    const [strategyInput, setStrategyInput] = useState('');
    const [strategyDropdownOpen, setStrategyDropdownOpen] = useState(false);
    const [allStrategies, setAllStrategies] = useState([]);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const strategyRef = useRef(null);

    // Parse AUM into number + unit
    const parseAum = (aum) => {
        if (!aum) return { number: '', unit: 'MM' };
        const str = String(aum).trim().toUpperCase();
        const match = str.match(/^([\d,.]+)\s*(BB?|MM?|T)?$/i);
        if (match) {
            const num = match[1].replace(/,/g, '');
            const u = match[2] || '';
            if (u.startsWith('B')) return { number: num, unit: 'B' };
            if (u.startsWith('T')) return { number: num, unit: 'T' };
            return { number: num, unit: 'MM' };
        }
        // Try as plain number
        const plain = parseFloat(String(aum).replace(/[,$]/g, ''));
        if (!isNaN(plain)) return { number: String(plain), unit: 'MM' };
        return { number: '', unit: 'MM' };
    };
    const initialAum = parseAum(formData.aum);
    const [aumNumber, setAumNumber] = useState(initialAum.number);
    const [aumUnit, setAumUnit] = useState(initialAum.unit);

    useEffect(() => {
        firmService.getAllStrategies().then(setAllStrategies).catch(() => {});
        const handleClickOutside = (e) => {
            if (strategyRef.current && !strategyRef.current.contains(e.target)) setStrategyDropdownOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!formData.name.trim()) { alert('Please enter a firm name'); return; }
        const aumValue = aumNumber ? `${aumNumber}${aumUnit}` : '';
        try {
            setSaving(true);
            await onSave({ ...formData, aum: aumValue, totalEmployees: parseInt(formData.totalEmployees) || 0, offices: formData.offices.filter(o => o.city && o.country) });
        } catch (error) { alert('Failed to save firm: ' + error.message); }
        finally { setSaving(false); }
    };

    const handleDelete = async () => {
        if (!window.confirm(`Delete "${firm.name}"? This will permanently remove the firm and all related data.`)) return;
        try { setDeleting(true); await onDelete(firm.id); }
        catch (error) { alert('Failed to delete firm: ' + error.message); }
        finally { setDeleting(false); }
    };

    const addStrategy = () => {
        const s = strategyInput.trim();
        if (s && !formData.strategies.includes(s)) { set('strategies', [...formData.strategies, s]); setStrategyInput(''); }
    };

    const updateOffice = (i, field, value) => {
        const o = [...formData.offices]; o[i] = { ...o[i], [field]: value }; set('offices', o);
    };

    return (
        <div className="flex-shrink-0 w-[520px] bg-white border-l border-gray-200 flex flex-col animate-[slidein_180ms_ease-out] overflow-hidden">
            <style>{`@keyframes slidein { from { opacity: 0; transform: translateX(20px);} to { opacity: 1; transform: translateX(0);} }`}</style>

                {/* Header */}
                <div className="px-5 py-4 border-b flex items-center justify-between flex-shrink-0">
                    <div>
                        <h3 className="font-semibold text-gray-900">{isEditing ? 'Edit Firm' : 'Add New Firm'}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">{isEditing ? `Editing ${firm.name}` : 'Create a new client firm profile'}</p>
                    </div>
                    <button onClick={onClose} className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

                    {/* Import from CSV — only when adding (not editing) */}
                    {!isEditing && onImportCsv && (
                        <div className="flex items-center justify-between gap-3 p-3 bg-emerald-50/60 border border-emerald-200 rounded-lg">
                            <div className="flex items-center gap-2 min-w-0">
                                <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                                <div className="min-w-0">
                                    <div className="text-xs font-medium text-gray-800">Adding several firms?</div>
                                    <div className="text-[11px] text-gray-500 truncate">Bulk import from a CSV file instead.</div>
                                </div>
                            </div>
                            <button type="button" onClick={onImportCsv}
                                className="flex-shrink-0 h-[30px] px-2.5 text-xs font-medium border border-emerald-300 text-emerald-700 bg-white rounded hover:bg-emerald-50">
                                Import CSV
                            </button>
                        </div>
                    )}

                    {/* Basic Info */}
                    <Section title="Basic Information">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2 sm:col-span-1">
                                <label className="block text-xs font-medium text-gray-600 mb-1">Firm Name *</label>
                                <input type="text" required value={formData.name} onChange={e => set('name', e.target.value)}
                                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" placeholder="Enter firm name" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Year Founded</label>
                                <input type="number" value={formData.founded} onChange={e => set('founded', parseInt(e.target.value))}
                                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" min="1900" max={new Date().getFullYear()} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Total Employees</label>
                                <input type="number" value={formData.totalEmployees} onChange={e => set('totalEmployees', e.target.value)}
                                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" placeholder="0" min="0" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">AUM</label>
                                <div className="flex">
                                    <span className="inline-flex items-center px-2.5 text-sm text-gray-500 bg-gray-50 border border-r-0 border-gray-300 rounded-l-lg">$</span>
                                    <input type="text" inputMode="decimal" value={aumNumber} onChange={e => { const v = e.target.value; if (v === '' || /^[\d,.]*\.?\d*$/.test(v)) setAumNumber(v); }}
                                        className="flex-1 min-w-0 px-3 py-1.5 text-sm border border-gray-300 focus:ring-2 focus:ring-emerald-500 focus:border-transparent" placeholder="0" />
                                    <select value={aumUnit} onChange={e => setAumUnit(e.target.value)}
                                        className="px-2 py-1.5 text-xs font-medium border border-l-0 border-gray-300 rounded-r-lg bg-gray-50 text-gray-700 focus:ring-2 focus:ring-emerald-500 cursor-pointer">
                                        <option value="MM">MM</option>
                                        <option value="B">B</option>
                                        <option value="T">T</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </Section>

                    {/* Web & LinkedIn */}
                    <Section title="Web & LinkedIn">
                        <div className="grid grid-cols-1 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">LinkedIn URL</label>
                                <div className="flex items-center gap-2">
                                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                                    <input type="text" value={formData.linkedinUrl || ''} onChange={e => set('linkedinUrl', e.target.value)}
                                        className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" placeholder="https://linkedin.com/company/..." />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Website</label>
                                <div className="flex items-center gap-2">
                                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>
                                    <input type="text" value={formData.website || ''} onChange={e => set('website', e.target.value)}
                                        className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" placeholder="https://example.com" />
                                </div>
                            </div>
                        </div>
                    </Section>

                    {/* Strategies */}
                    <Section title="Investment Strategies">
                        <div className="relative mb-2" ref={strategyRef}>
                            <input type="text" value={strategyInput}
                                onChange={e => { setStrategyInput(e.target.value); setStrategyDropdownOpen(true); }}
                                onFocus={() => setStrategyDropdownOpen(true)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') { e.preventDefault(); addStrategy(); }
                                    if (e.key === 'Escape') setStrategyDropdownOpen(false);
                                }}
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                placeholder="Search or type a new strategy..." />
                            {strategyDropdownOpen && (() => {
                                const q = strategyInput.toLowerCase().trim();
                                const filtered = allStrategies.filter(s =>
                                    s.toLowerCase().includes(q) && !formData.strategies.includes(s)
                                ).slice(0, 8);
                                const showCreate = q && !allStrategies.some(s => s.toLowerCase() === q) && !formData.strategies.some(s => s.toLowerCase() === q);
                                if (filtered.length === 0 && !showCreate) return null;
                                return (
                                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                        {filtered.map(s => (
                                            <button key={s} type="button" onClick={() => { set('strategies', [...formData.strategies, s]); setStrategyInput(''); setStrategyDropdownOpen(false); }}
                                                className="w-full px-3 py-1.5 text-left text-sm hover:bg-emerald-50 flex items-center gap-2">
                                                <span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
                                                {s}
                                            </button>
                                        ))}
                                        {showCreate && (
                                            <button type="button" onClick={() => { addStrategy(); setStrategyDropdownOpen(false); }}
                                                className="w-full px-3 py-1.5 text-left text-sm hover:bg-green-50 flex items-center gap-2 border-t border-gray-100 text-green-700">
                                                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                                Create "{strategyInput.trim()}"
                                            </button>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                        <div className="flex flex-wrap gap-1.5 min-h-[28px]">
                            {formData.strategies.map((s, i) => (
                                <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs font-medium border border-emerald-200">
                                    {s}
                                    <button type="button" onClick={() => set('strategies', formData.strategies.filter(x => x !== s))}
                                        className="text-emerald-400 hover:text-emerald-700">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                </span>
                            ))}
                            {formData.strategies.length === 0 && <span className="text-xs text-gray-400">No strategies added</span>}
                        </div>
                    </Section>

                    {/* Offices */}
                    <Section title="Office Locations" action={
                        <button type="button" onClick={() => set('offices', [...formData.offices, { city: '', state: '', country: '', isPrimary: false }])}
                            className="text-[10px] font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-0.5">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                            Add Office
                        </button>
                    }>
                        <div className="space-y-2">
                            {formData.offices.map((office, i) => (
                                <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                                    <div className="flex-1">
                                        <LocationSearch
                                            value={office.city ? { city: office.city, state: office.state || '', country: office.country || '' } : null}
                                            onChange={(loc) => {
                                                const o = [...formData.offices];
                                                if (loc) {
                                                    o[i] = { ...o[i], city: loc.city, state: loc.state || '', country: loc.country || '' };
                                                } else {
                                                    o[i] = { ...o[i], city: '', state: '', country: '' };
                                                }
                                                set('offices', o);
                                            }}
                                            placeholder="Search city..."
                                        />
                                    </div>
                                    <label className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap">
                                        <input type="checkbox" checked={office.isPrimary} onChange={e => updateOffice(i, 'isPrimary', e.target.checked)}
                                            className="rounded border-gray-300 accent-emerald-600 focus:ring-emerald-500 w-3.5 h-3.5" /> HQ
                                    </label>
                                    {formData.offices.length > 1 && (
                                        <button type="button" onClick={() => set('offices', formData.offices.filter((_, j) => j !== i))}
                                            className="text-gray-400 hover:text-red-500 p-0.5">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Section>

                    {/* Compensation */}
                    <Section title="Compensation">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Payout Structure</label>
                                <input type="text" value={formData.payoutStructure} onChange={e => set('payoutStructure', e.target.value)}
                                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" placeholder="e.g., 2 and 20" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Bonus Date</label>
                                <input type="date" value={formData.bonusDate} onChange={e => set('bonusDate', e.target.value)}
                                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">PnL Payout Frequency</label>
                                <select value={formData.pnlPayoutFrequency || ''} onChange={e => set('pnlPayoutFrequency', e.target.value)}
                                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent">
                                    <option value="">Select frequency</option>
                                    <option value="quarterly">Quarterly</option>
                                    <option value="triannual">Triannual</option>
                                    <option value="biannual">Biannual</option>
                                    <option value="annual">Annual</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Put Up Capital</label>
                                <div className="flex items-center gap-4 h-[34px]">
                                    <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                                        <input type="radio" name="putUpCapital" checked={formData.putUpCapital === true}
                                            onChange={() => set('putUpCapital', true)} className="accent-emerald-600" /> Yes
                                    </label>
                                    <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                                        <input type="radio" name="putUpCapital" checked={formData.putUpCapital === false}
                                            onChange={() => set('putUpCapital', false)} className="accent-emerald-600" /> No
                                    </label>
                                </div>
                            </div>
                        </div>
                    </Section>

                    {/* Notes */}
                    <Section title="Notes">
                        <textarea value={formData.notes} onChange={e => set('notes', e.target.value)}
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                            rows="2" placeholder="Additional notes about the firm..." />
                    </Section>
                </div>

                {/* Footer */}
                <div className="px-5 py-3 border-t flex items-center justify-between flex-shrink-0 bg-gray-50">
                    <div>
                        {isEditing && onDelete && (
                            <button type="button" onClick={handleDelete} disabled={deleting}
                                className="text-xs text-red-600 hover:text-red-700 font-medium disabled:opacity-50">
                                {deleting ? 'Deleting...' : 'Delete Firm'}
                            </button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button type="button" onClick={onClose}
                            className="px-3.5 py-1.5 text-xs font-medium border border-gray-300 rounded hover:bg-gray-50 text-gray-700">Cancel</button>
                        <button type="button" onClick={handleSubmit} disabled={saving}
                            className="px-3.5 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
                            {saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Add Firm')}
                        </button>
                    </div>
                </div>
        </div>
    );
}
